import React, {useState, useEffect} from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import { Box, Card, Divider, Grid2 as Grid, Link, Stack, Typography, useTheme } from '@mui/material';
import Tab from '@mui/material/Tab';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';

//import schedules
import ReportAmendReasonBlock from '../components/ReportAmendReasonBlock';
import ReceiptsScheduleTable from '../components/ReceiptsScheduleTable';
import { ReceiptItem } from '../../../types/reports/receiptItem';
import { ExpenditureItem } from '../../../types/reports/expenditureItem';
import ExpendituresScheduleTable from '../components/ExpendituresScheduleTable';
import ReportAmendedReasonBlock from '../components/ReportAmendedReasonBlock';
import IEPACYearEndReportContent from '../../../types/reports/iepacYearEndReportContent';
import { LiabilityItem } from '../../../types/reports/liabilityItem';
import changeLogCategory from '../../../types/reports/changeLogCategory';
import LiabilitiesScheduleTable from '../components/LiabilitiesScheduleTable';
import ChangeLogScheduleTable from '../components/ChangeLogScheduleTable';
import { SubvendorPaymentItem } from '../../../types/reports/subvendorPaymentItem';
import SubvendorPaymentsScheduleTable from '../components/SubvendorPaymentsScheduleTable';
import ChangeLogHistory from '../components/ChangeLogHistory';
import reportChangeLog from '../../../types/reports/reportChangeLog';

interface ReportProps {
   reportId: number;
   currentReport?: any;
}

const IEPACYearEndReport = (reportProps: ReportProps): JSX.Element => {

    const theme = useTheme();
    const { showBoundary } = useErrorBoundary();  
    const [scheduleValue, setScheduleValue] = useState('summary');
    const fieldWidth = { xs: "50%", sm: "40%", md: "35%" };

    const [receiptsRows, setReceiptsRows] = useState<ReceiptItem[]>();
    const [inKindsRows, setInkindsRows] = useState<ReceiptItem[]>();
    const [expendituresRows, setExpendituresRows] = useState<ExpenditureItem[]>();
    const [subvendorPaymentsRows, setSubvendorPaymentsRows] = useState<SubvendorPaymentItem[]>();
    const [liabilitiesRows, setLiabilitiesRows] = useState<LiabilityItem[]>();
    const [diffs, setDiffs] = useState<changeLogCategory[]>();
    const [report, setReport] = useState<IEPACYearEndReportContent>();
    const [reportChangeLogs, setReportChangeLogs] = useState<reportChangeLog[]>();

    useEffect(() => {
        if(reportProps.currentReport){
            let report = reportProps.currentReport;
            setScheduleValue('summary');
            setReport(report);
            setReceiptsRows(report.receipts);
            setExpendituresRows(report.expenditures);
            setInkindsRows(report.inkindContributions);
            setLiabilitiesRows(report.liabilityRelatedItems);
            setSubvendorPaymentsRows(report.subvendorPayments);
            setDiffs(report.diffs);
        }
        else{
            getFullReport();
        }
        getReportDiffs();
    }, [reportProps.reportId])

    const getFullReport = async () => {

        try{
            await fetch(`${process.env.REACT_APP_API_BASE_URL}/report/${reportProps.reportId}`)
            .then(result => {
            if (!result.ok) {
                showBoundary(new Error('Failed to get IEPAC year end report content.'));
            }
            return result.json();
        })
        .then(result => {

            let report = result;
            setScheduleValue('summary');
            setReport(report);
            setReceiptsRows(report.receipts);
            setExpendituresRows(report.expenditures);
            setInkindsRows(report.inkindContributions);
            setLiabilitiesRows(report.liabilityRelatedItems);
            setSubvendorPaymentsRows(report.subvendorPayments);
            setDiffs(report.diffs);
            return;
        });
        }catch(error){
            console.error('Uncaught Error:', error);
        }
    }


    const getReportDiffs = async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/report/diffs/${reportProps.reportId}`);
            if (!response.ok) {
                showBoundary(new Error('Failed to get report change log.'));
            }
            const diffs = await response.json();
            setReportChangeLogs(diffs);
        } catch (error) {
            console.error('Uncaught Error:', error);
        }
    }

    const handleScheduleChange = (event: React.SyntheticEvent, newValue: string) => {
        setScheduleValue(newValue);
    };

  return (
    <Box sx={{ width: '100%', typography: 'body1' }}>
      <TabContext value={scheduleValue}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <TabList onChange={handleScheduleChange} aria-label="periodic report schedules">
            <Tab className="report-tab-label" label="Summary" value="summary" />
            {
                (report?.receipts?.length ?? 0) > 0 &&
                <Tab className="report-tab-label" label={`Receipts [${report?.receipts?.length}]`} value="receipts" />
            }
            {
                (report?.expenditures?.length ?? 0) > 0 &&
                <Tab className="report-tab-label" label={`Expenditures [${report?.expenditures?.length}]`} value="expenditures" />
            }
            {
                (report?.inkindContributions?.length ?? 0) > 0 &&
                <Tab className="report-tab-label" label={`In-kind Contributions [${report?.inkindContributions?.length}]`} value="inkinds" />
            }
            {
                (report?.liabilities?.length ?? 0) > 0 &&
                <Tab className="report-tab-label" label={`Liabilities [${report?.liabilityRelatedItems?.length}]`} value="liabilities" />
            }
            {
                (report?.subvendorPayments?.length ?? 0) > 0 &&
                 <Tab className="report-tab-label" label={`Subvendor Payments [${report?.subvendorPayments?.length}]`} value="subvendors" />
            }
            {
                 report?.diffs && report.isAmendment && report.isAmended && // not the latest report, staying the same
                 <Tab className="report-tab-label" label={`Amended Items`} value="changes"/>
            }
            {
                  !report?.isAmended && reportChangeLogs && reportChangeLogs.length > 0 && // latest report
                 <Tab className="report-tab-label" label={`Change Log`} value="logs"/>
            }
          </TabList>
        </Box>
        <TabPanel value="summary">
            <Grid container spacing={2}>
                {/*Filer Info*/}
                <Grid size={12}>
                    <Box
                        component={Card}
                        boxShadow={0}
                        sx={{ bgcolor: 'alternate.light' }}
                        border={`1px solid ${theme.palette.divider}`}
                    >
                        <Box padding={{ xs: 2, sm: 3 }}>
                            <Stack direction="row" spacing={1} marginBottom={2}>
                                <Typography component={"i"} variant={'subtitle2'} width={fieldWidth}>
                                     Filer:
                                </Typography>
                                <Typography component={Link} underline='hover' target="_blank" color="primary" href={`/filers?q=${report?.cpfId}`} variant={'subtitle2'} fontWeight={550} align='left'>
                                    {report?.filerFullName} ({report?.cpfId})
                                </Typography>
                            </Stack>
                            <Stack direction="row" spacing={1} marginBottom={2}>
                                <Typography component={"i"} variant={'subtitle2'} width={fieldWidth}>
                                     Filed On:
                                </Typography>
                                <Typography variant={'subtitle2'} align='left' width={fieldWidth}>
                                     {report?.dateFiled}
                                </Typography>
                            </Stack>
                            <Stack direction="row" spacing={1} marginBottom={2}>
                                <Typography component={"i"} variant={'subtitle2'} width={fieldWidth}>
                                    Reporting Period:
                                </Typography>
                                <Typography variant={'subtitle2'} color="primary" align='left' width={fieldWidth}>
                                     {report?.reportingPeriod}
                                </Typography>
                            </Stack>
                            <Stack direction="row" spacing={1}>
                                <Typography component={"i"} variant={'subtitle2'} width={fieldWidth}>
                                    Bank Name:
                                </Typography>
                                <Typography variant={'subtitle2'} align='left' width={fieldWidth}>
                                     {report?.bankName}
                                </Typography>
                            </Stack>
                        </Box>
                    </Box>
                </Grid>
                {/*Amendment Reason*/}
                {
                    report?.isAmendment && 
                    <ReportAmendReasonBlock reason={report?.amendmentReason} previousReportId={report?.previousReportId}></ReportAmendReasonBlock>
                }
                {/*Being Amended*/}
                {
                    report?.isAmended && 
                    <ReportAmendedReasonBlock nextReportId={report?.nextReportId}></ReportAmendedReasonBlock>
                }
                {/*Financial Summary*/}
                <Grid size={12}>
                    <Box
                        component={Card}
                        boxShadow={0}
                        sx={{ bgcolor: 'alternate.light' }}
                        border={`1px solid ${theme.palette.divider}`}
                    >
                        <Box padding={{ xs: 2, sm: 3 }}>
                            <Stack direction="row" spacing={1} marginBottom={2} display={'flex'} justifyContent={'space-between'}>
                                <Typography component={"i"} variant={'subtitle2'}>
                                    Beginning Balance:
                                </Typography>
                                <Typography variant={'subtitle2'} align='right'>
                                     {report?.startBalance}
                                </Typography>
                            </Stack>
                            <Stack direction="row" spacing={1} marginBottom={2} display={'flex'} justifyContent={'space-between'}>
                                <Typography component={"i"} variant={'subtitle2'}>
                                    Total Receipts previously reported:
                                </Typography>
                                <Typography variant={'subtitle2'} align='right'>
                                     {report?.previouslyReportedReceiptTotal}
                                </Typography>
                            </Stack>
                            <Stack direction="row" spacing={1} marginBottom={2} display={'flex'} justifyContent={'space-between'}>
                                <Typography component={"i"} variant={'subtitle2'}>
                                    Total Itemized Receipts:
                                </Typography>
                                <Typography variant={'subtitle2'} align='right'>
                                     {report?.receiptItemizedTotal}
                                </Typography>
                            </Stack>
                            <Stack direction="row" spacing={1} marginBottom={2} display={'flex'} justifyContent={'space-between'}>
                                <Typography component={"i"} variant={'subtitle2'}>
                                    Total Unitemized Receipts:
                                </Typography>
                                <Typography variant={'subtitle2'} align='right'>
                                     {report?.receiptUnitemizedTotal}
                                </Typography>
                            </Stack>
                            <Stack direction="row" spacing={1} marginBottom={2} display={'flex'} justifyContent={'space-between'}>
                                <Typography component={"i"} variant={'subtitle2'} fontWeight={550}>
                                    Total Receipts this period:
                                </Typography>
                                <Typography variant={'subtitle2'} align='right' fontWeight={550}>
                                     {report?.receiptTotal}
                                </Typography>
                            </Stack>
                            <Stack direction="row" spacing={1} marginBottom={2} display={'flex'} justifyContent={'space-between'}>
                                <Typography component={"i"} variant={'subtitle2'}>
                                    Subtotal:
                                </Typography>
                                <Typography variant={'subtitle2'} align='right'>
                                     {report?.subtotal}
                                </Typography>
                            </Stack>
                            <Stack direction="row" spacing={1} marginBottom={2} display={'flex'} justifyContent={'space-between'}>
                                <Typography component={"i"} variant={'subtitle2'}>
                                    Total Expenditures previously reported:
                                </Typography>
                                <Typography variant={'subtitle2'} align='right'>
                                     {report?.previouslyReportedExpenditureTotal}
                                </Typography>
                            </Stack>
                            <Stack direction="row" spacing={1} marginBottom={2} display={'flex'} justifyContent={'space-between'}>
                                <Typography component={"i"} variant={'subtitle2'}>
                                    Total Itemized Expenditures:
                                </Typography>
                                <Typography variant={'subtitle2'} align='right'>
                                     {report?.expenditureItemizedTotal}
                                </Typography>
                            </Stack>
                            <Stack direction="row" spacing={1} marginBottom={2} display={'flex'} justifyContent={'space-between'}>
                                <Typography component={"i"} variant={'subtitle2'}>
                                        Total Unitemized Expenditures:
                                </Typography>
                                <Typography variant={'subtitle2'} align='right'>
                                     {report?.expenditureUnitemizedTotal}
                                </Typography>
                            </Stack>
                            <Stack direction="row" spacing={1} marginBottom={2} display={'flex'} justifyContent={'space-between'}>
                                <Typography component={"i"} variant={'subtitle2'} fontWeight={550}>
                                    Total Expenditures this period:
                                </Typography>
                                <Typography variant={'subtitle2'} align='right' fontWeight={550}>
                                     {report?.expenditureTotal}
                                </Typography>
                            </Stack>
                            <Stack direction="row" spacing={1} marginBottom={2} display={'flex'} justifyContent={'space-between'}>
                                <Typography component={"i"} variant={'subtitle2'}>
                                    Ending Balance:
                                </Typography>
                                <Typography variant={'subtitle2'} align='right'>
                                     {report?.endBalance}
                                </Typography>
                            </Stack>
                            <Divider />
                             <Stack direction="row" spacing={1} marginBottom={2} marginTop={2} display={'flex'} justifyContent={'space-between'}>
                                <Typography component={"i"} variant={'subtitle2'}>
                                    Previously Reported In-kind Contributions:
                                </Typography>
                                <Typography variant={'subtitle2'} align='right'>
                                     {report?.previouslyReportedInkindTotal}
                                </Typography>
                            </Stack>
                             <Stack direction="row" spacing={1} marginBottom={2} marginTop={2} display={'flex'} justifyContent={'space-between'}>
                                <Typography component={"i"} variant={'subtitle2'}>
                                    Itemized Inkind Contributions:
                                </Typography>
                                <Typography variant={'subtitle2'} align='right'>
                                     {report?.inkindItemizedTotal}
                                </Typography>
                            </Stack>
                             <Stack direction="row" spacing={1} marginBottom={2} marginTop={2} display={'flex'} justifyContent={'space-between'}>
                                <Typography component={"i"} variant={'subtitle2'}>
                                    Unitemized Inkind Contributions:
                                </Typography>
                                <Typography variant={'subtitle2'} align='right'>
                                     {report?.inkindUnitemizedTotal}
                                </Typography>
                            </Stack>
                            <Stack direction="row" spacing={1} marginBottom={2} marginTop={2} display={'flex'} justifyContent={'space-between'}>
                                <Typography component={"i"} variant={'subtitle2'} fontWeight={550}>
                                    Total Inkind Contributions:
                                </Typography>
                                <Typography variant={'subtitle2'} align='right' fontWeight={550}>
                                     {report?.inkindTotal}
                                </Typography>
                            </Stack>
                            <Stack direction="row" spacing={1} marginBottom={2} display={'flex'} justifyContent={'space-between'}>
                                <Typography component={"i"} variant={'subtitle2'}>
                                    Total Outstanding Liabilities:
                                </Typography>
                                <Typography variant={'subtitle2'} align='right'>
                                     {report?.liabilityItemizedTotal}
                                </Typography>
                            </Stack>
                        </Box>
                    </Box>
                </Grid>
            </Grid>
        </TabPanel>
        <TabPanel value="receipts">
            <ReceiptsScheduleTable tableRows={receiptsRows}></ReceiptsScheduleTable>
            <Box padding={{ xs: 2, sm: 3 }}>
                <Stack direction="row" spacing={1} marginBottom={2} display={'flex'} justifyContent={'space-between'}>
                    <Typography variant={'subtitle2'}>
                        Total Itemized Receipts:
                    </Typography>
                    <Typography variant={'subtitle2'} fontWeight={550} align='right'>
                        {report?.receiptItemizedTotal}
                    </Typography>
                </Stack>
                <Stack direction="row" spacing={1} marginBottom={2} display={'flex'} justifyContent={'space-between'}>
                    <Typography variant={'subtitle2'}>
                        Total Unitemized Receipts:
                    </Typography>
                    <Typography variant={'subtitle2'} fontWeight={550} align='right'>
                        {report?.receiptUnitemizedTotal}
                    </Typography>
                </Stack>
                <Stack direction="row" spacing={1} marginBottom={2} display={'flex'} justifyContent={'space-between'}>
                    <Typography variant={'subtitle2'}>
                        Total Receipts:
                    </Typography>
                    <Typography variant={'subtitle2'} fontWeight={550} align='right'>
                        {report?.receiptTotal}
                    </Typography>
                </Stack>
            </Box>
        </TabPanel>
        <TabPanel value="expenditures">
            <ExpendituresScheduleTable tableRows={expendituresRows}></ExpendituresScheduleTable>
            <Box padding={{ xs: 2, sm: 3 }}>
                 <Stack direction="row" spacing={1} marginBottom={2} display={'flex'} justifyContent={'space-between'}>
                    <Typography variant={'subtitle2'}>
                        Itemized Total:
                    </Typography>
                    <Typography variant={'subtitle2'} fontWeight={550} align='right'>
                        {report?.expenditureItemizedTotal}
                    </Typography>
                </Stack>
                <Stack direction="row" spacing={1} marginBottom={2} display={'flex'} justifyContent={'space-between'}>
                    <Typography variant={'subtitle2'}>
                        Unitemized Total:
                    </Typography>
                    <Typography variant={'subtitle2'} fontWeight={550} align='right'>
                        {report?.expenditureUnitemizedTotal}
                    </Typography>
                </Stack>
                <Stack direction="row" spacing={1} marginBottom={2} display={'flex'} justifyContent={'space-between'}>
                    <Typography variant={'subtitle2'}>
                        Expenditure Total:
                    </Typography>
                    <Typography variant={'subtitle2'} fontWeight={550} align='right'>
                        {report?.expenditureTotal}
                    </Typography>
                </Stack>
            </Box>
        </TabPanel>
        <TabPanel value="inkinds">
            <ReceiptsScheduleTable tableRows={inKindsRows}></ReceiptsScheduleTable>
            <Box padding={{ xs: 2, sm: 3 }}>
                 <Stack direction="row" spacing={1} marginBottom={2} display={'flex'} justifyContent={'space-between'}>
                    <Typography variant={'subtitle2'}>
                        Previously Reported In-kind Contributions:
                    </Typography>
                    <Typography variant={'subtitle2'} fontWeight={550} align='right'>
                        {report?.previouslyReportedInkindTotal}
                    </Typography>
                </Stack>
                <Stack direction="row" spacing={1} marginBottom={2} display={'flex'} justifyContent={'space-between'}>
                    <Typography variant={'subtitle2'}>
                        Itemized Total:
                    </Typography>
                    <Typography variant={'subtitle2'} fontWeight={550} align='right'>
                        {report?.inkindItemizedTotal}
                    </Typography>
                </Stack>
                <Stack direction="row" spacing={1} marginBottom={2} display={'flex'} justifyContent={'space-between'}>
                    <Typography variant={'subtitle2'}>
                        Unitemized Total:
                    </Typography>
                    <Typography variant={'subtitle2'} fontWeight={550} align='right'>
                        {report?.inkindUnitemizedTotal}
                    </Typography>
                </Stack>
                <Stack direction="row" spacing={1} marginBottom={2} display={'flex'} justifyContent={'space-between'}>
                    <Typography variant={'subtitle2'}>
                        Total In-kind Contributions:
                    </Typography>
                    <Typography variant={'subtitle2'} fontWeight={550} align='right'>
                        {report?.inkindTotal}
                    </Typography>
                </Stack>
            </Box>
        </TabPanel>
        <TabPanel value="liabilities">
            <LiabilitiesScheduleTable items={liabilitiesRows}></LiabilitiesScheduleTable>
            <Box padding={{ xs: 2, sm: 3 }}>
                <Stack direction="row" spacing={1} marginBottom={2} display={'flex'} justifyContent={'space-between'}>
                    <Typography variant={'subtitle2'}>
                        Total Liabilities:
                    </Typography>
                    <Typography variant={'subtitle2'} fontWeight={550} align='right'>
                        {report?.liabilityItemizedTotal}
                    </Typography>
                </Stack>
            </Box>
        </TabPanel>
        <TabPanel value="subvendors">
            <SubvendorPaymentsScheduleTable subVendorPayments={subvendorPaymentsRows}></SubvendorPaymentsScheduleTable>
        </TabPanel>
        <TabPanel value="changes">
            <ChangeLogScheduleTable changes={diffs}></ChangeLogScheduleTable>
        </TabPanel>
        <TabPanel value="logs">
            <ChangeLogHistory changes={reportChangeLogs}></ChangeLogHistory>
        </TabPanel>
      </TabContext>
    </Box>
  );
}

export default IEPACYearEndReport;